// Lib
import React from 'react';
import PropTypes from 'prop-types';

// Utils
import * as pointsLib from '../../common/maths/geometry/point';
import { now, noLonger } from '../utils/react/propsComparisons';
import {
    calculatePositionDelta,
    calculateRotation,
    getHasOutstandingChanges,
    getNextShadowIntensityFading,
    getShouldRotateElements,
    NO_ROTATION,
} from '../element/dnd/utils/elementDragAnimationUtils';
import rafThrottle from '../../common/utils/lib/rafThrottle';
import { asObject, length, prop } from '../../common/utils/immutableHelper';
import { getIosSafeCssRotation, getIosSafeCssTranslation } from '../utils/cssUtil';

const nowHasDropState = now('dropState');
const noLongerHasDropState = noLonger('dropState');

export default (DecoratedComponent) => {
    class canvasElementDropAnimationStateManager extends React.Component {
        constructor(props) {
            super(props);

            this.endAnimationTimeoutId = null;

            this.state = {
                animating: false,
            };

            this.canvasElementRef = React.createRef();
        }

        componentWillMount() {
            if (this.props.dropState) this.startAnimation(this.props.dropState);
        }

        componentWillReceiveProps(nextProps) {
            if (nowHasDropState(this.props, nextProps)) {
                this.dragStarted = false;
                this.startAnimation(nextProps.dropState);
            }
            if (noLongerHasDropState(this.props, nextProps)) {
                this.dragStarted = true;
            }
        }

        componentWillUnmount() {
            this.throttledAnimation.cancel();
            if (this.endAnimationTimeoutId) clearTimeout(this.endAnimationTimeoutId);
        }

        startAnimation = (dropState) => {
            if (this.endAnimationTimeoutId) {
                this.cleanUpAfterAnimation();
                clearTimeout(this.endAnimationTimeoutId);
            }

            const animatingDrag = prop('animatingDrag', dropState);

            // If the user hasn't enabled 3D effects then don't do any animation
            if (!animatingDrag) return;

            const dropStateOffset = prop('dropOffset', dropState);
            const dropStateRotation = prop('elementRotation', dropState);
            const targetOffset = prop('targetOffset', dropState);
            this.shadowIntensity = prop('shadowIntensity', dropState);
            this.maxRotation = prop('maxRotation', dropState);

            // Leave off from where the drag animation was, but snap to the grid
            let previousOffset = dropStateOffset ? asObject(dropStateOffset) : NO_ROTATION;
            previousOffset = targetOffset ? pointsLib.translate(targetOffset, previousOffset) : previousOffset;
            const previousRotation = dropStateRotation ? asObject(dropStateRotation) : NO_ROTATION;

            this.animatedElementCount = length(prop('droppedElementIds', dropState));

            this.throttledAnimation(previousOffset, previousRotation, this.shadowIntensity, this.animatedElementCount);

            this.animating = true;
            this.setState({ animating: true });

            if (this.canvasElementRef.current) {
                this.canvasElementRef.current.classList.add('drag-end');

                const dragHandle = this.canvasElementRef.current.querySelector('.drag-handle');

                if (!dragHandle) return;

                // Add the temporary drop shadow element to the DOM so it can fade out the shadow
                const dropShadow = document.createElement('div');
                dropShadow.className = 'drop-shadow';
                dropShadow.style.opacity = this.shadowIntensity;

                dragHandle.insertBefore(dropShadow, dragHandle.firstChild);
            }
        };

        cleanUpAfterAnimation = () => {
            this.animating = false;

            if (!this.canvasElementRef.current) return;

            this.canvasElementRef.current.classList.remove('drag-end');

            const dropShadows = this.canvasElementRef.current.querySelectorAll('.drop-shadow');
            dropShadows && dropShadows.forEach((dropShadow) => dropShadow.remove());
        };

        // Retrieves the starting state from the state
        continueAnimation = () => {
            this.throttledAnimation(
                this.previousOffset,
                this.previousRotation,
                this.shadowIntensity,
                this.animatedElementCount,
            );
        };

        animate = (previousOffset, previousRotation, shadowIntensity, animatedElementCount) => {
            const { element } = this.props;

            if (!element) return;

            // Can always keep the dnd 3d effects enabled here because if the user hasn't
            // enabled them the max rotation will be 0 anyway
            const shouldRotateElements = getShouldRotateElements({
                areDnd3dEffectsEnabled: true,
                element,
                elementCount: animatedElementCount,
            });

            // Linearly interpolate the position to smooth out the motion
            const positionDelta = calculatePositionDelta({
                targetPosition: { x: 0, y: 0 },
                previousPosition: previousOffset,
                shouldRotateElements,
            });

            let rotation = shouldRotateElements
                ? calculateRotation({ positionDelta, previousRotation, maxRotation: this.maxRotation })
                : NO_ROTATION;

            let offset = {
                x: previousOffset.x + positionDelta.x,
                y: previousOffset.y + positionDelta.y,
            };

            this.shadowIntensity = getNextShadowIntensityFading(shadowIntensity);
            const hasOutstandingChanges = getHasOutstandingChanges(rotation, offset) || this.shadowIntensity > 0.1;

            if (!hasOutstandingChanges) {
                offset = NO_ROTATION;
                rotation = NO_ROTATION;
            }

            this.previousOffset = offset;
            this.previousRotation = rotation;

            // Takes the rendering out of react's hands for performance
            this.renderAnimation();

            if (hasOutstandingChanges) {
                this.continueAnimation();
            } else {
                // NOTE: The cleanup must happen before setting the state, otherwise
                //  new cards will not be in the editing state after drop
                this.cleanUpAfterAnimation();
                this.setState({ animating: false });
            }
        };

        /**
         * Uses vanilla JavaScript to render the animation to make it more performant.
         */
        renderAnimation = () => {
            if (!this.canvasElementRef || !this.canvasElementRef.current) return;

            // Ensure cleanup happens when there's no offset
            if (this.previousOffset === NO_ROTATION) {
                this.canvasElementRef.current.style.removeProperty('transform');
            } else {
                this.canvasElementRef.current.style.transform = getIosSafeCssTranslation(this.previousOffset);
            }

            const elementNode = this.canvasElementRef.current.querySelector('.Element');
            const dropShadow = elementNode.querySelector('.drop-shadow');

            // Manually remove the dragging class so we can avoid having to use react to re-render the element.
            if (!this.dragStarted) elementNode.classList.remove('dragging');

            if (this.previousRotation === NO_ROTATION) {
                elementNode && elementNode.style.removeProperty('transform');
            } else {
                const rotationTransform = getIosSafeCssRotation(this.previousRotation);
                elementNode && (elementNode.style.transform = rotationTransform);
            }

            if (!dropShadow) return;

            dropShadow.style.opacity = this.shadowIntensity;
        };

        throttledAnimation = rafThrottle(this.animate);

        render() {
            const { animating } = this.state;
            return (
                <DecoratedComponent
                    {...this.props}
                    dropState={null} // Setting dropState to null to prevent unnecessary renders in the child components
                    canvasElementRef={this.canvasElementRef}
                    animating={this.animating || animating}
                    startDragPreviewShadowAnimation={this.startAnimation}
                />
            );
        }
    }

    canvasElementDropAnimationStateManager.propTypes = {
        dropState: PropTypes.object,
        element: PropTypes.object,
    };

    return canvasElementDropAnimationStateManager;
};
